import { Component, OnInit, inject } from '@angular/core';

import { Router } from '@angular/router';
import { UserService } from '../../../@application/services/user.service';
import { PathEnum } from '../../../@application/enum/path.enum';
import { UserMeResponse } from '../../../@domain/user-details.model';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { KeycloakService } from 'keycloak-angular';
import { BrandingService } from '../../../@application/services/branding.service';

@Component({
    selector: 'app-initializing',
    imports: [NzResultModule, NzButtonModule, NzSpinModule],
    templateUrl: './initializing.component.html',
    styleUrls: ['./initializing.component.scss']
})
export class InitializingComponent implements OnInit {
  private userService = inject(UserService);
  private router = inject(Router);
  private keycloakService = inject(KeycloakService);
  private brandingService = inject(BrandingService);

  loadingMessage = 'Initializing your workspace...';
  hasError = false;
  errorMessage = '';
  errorTitle = '';
  errorIcon: 'success' | 'error' | 'info' | 'warning' = 'error';
  isLoading = true;

  ngOnInit(): void {
    this.initializeUserData();
  }

  private initializeUserData(): void {
    this.isLoading = true;
    this.userService.getUserDetails().subscribe({
      next: (response: UserMeResponse) => {
        // Store permissions in session storage
        if (response.permissions && response.permissions.length > 0) {
          sessionStorage.setItem('permissions', JSON.stringify(response.permissions));
        }

        // Store roles in session storage
        if (response.roles && response.roles.length > 0) {
          sessionStorage.setItem('roles', JSON.stringify(response.roles));
        }

        // Store user details in session storage
        if (response.tokenUserDetails) {
          sessionStorage.setItem('userDetails', JSON.stringify(response.tokenUserDetails));
        }

        // Store tenant info in session storage
        if (response.tenantInfoDetails && response.tenantInfoDetails.length > 0) {
          sessionStorage.setItem('tenantInfo', JSON.stringify(response.tenantInfoDetails));
          const activeTenant = response.tenantInfoDetails.find(t => t.isDefault) ?? response.tenantInfoDetails[0];
          if (activeTenant?.brandHex) {
            sessionStorage.setItem('brandHex', activeTenant.brandHex);
            this.brandingService.applyBrand(activeTenant.brandHex);
          }
          if (activeTenant?.logoUrl) sessionStorage.setItem('logoUrl', activeTenant.logoUrl);
        }

        this.isLoading = false;
        // Navigate to dashboard after successful initialization
        this.router.navigate([PathEnum.Home, PathEnum.DashBoard]);
      },
      error: (error: any) => {
        console.error('Failed to initialize user data:', error);
        this.handleInitializationError(error);
      }
    });
  }

  private handleInitializationError(error: any): void {
    this.isLoading = false;
    this.hasError = true;

    switch (error.status) {
      case 402:
        this.setErrorState(
          'Subscription Expired',
          'warning',
          error.error?.Message || 'Your token balance has expired. Please refresh your tokens to continue.'
        );
        break;
      case 500:
        this.setErrorState(
          'Connection Issue',
          'error',
          'We are having trouble connecting to our servers. This might be a temporary issue. Please try again in a moment.'
        );
        break;
      case 503:
        this.setErrorState(
          'Service Unavailable',
          'warning',
          'Our service is temporarily unavailable. We are working on it! Please try again shortly.'
        );
        break;
      case 404:
        this.setErrorState(
          'Resource Not Found',
          'error',
          'We could not find the resource you are looking for. Please contact support if this issue persists.'
        );
        break;
      case 403:
        this.setErrorState(
          'Access Denied',
          'error',
          'You do not have permission to access this workspace. Please contact your administrator.'
        );
        break;
      case 401:
        this.setErrorState(
          'Authentication Required',
          'warning',
          'Your session has expired. Please log in again to continue.'
        );
        break;
      default:
        this.setErrorState(
          error.error?.message || error.message ? 'Initialization Failed' : '⚠️ Oops! Something Went Wrong',
          'error',
          error.error?.message || error.message || 'We encountered an unexpected error while setting up your workspace. Please try again or contact support if the problem persists.'
        );
    }
  }

  private setErrorState(title: string, icon: 'success' | 'error' | 'info' | 'warning', message: string): void {
    this.errorTitle = title;
    this.errorIcon = icon;
    this.errorMessage = message;
  }

  retry(): void {
    this.hasError = false;
    this.errorMessage = '';
    this.errorTitle = '';
    this.errorIcon = 'error';
    this.loadingMessage = 'Initializing your workspace...';
    this.initializeUserData();
  }

  logout(): void {
    // Clear session storage and logout via keycloak
    sessionStorage.clear();
    this.keycloakService.logout(window.location.origin);
  }
}

